import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import {
  DocumentType,
  GetBusinessEntityDetailsQuery,
  GoodInput,
  GoodProfile,
  LoadType,
  NoteAccessLevel,
  ShipmentCommodityType,
  ShipmentLocationType,
  ShipmentNoteInput,
  TrailerType,
  TransactionInput,
  useGetGroupsQuery,
  useGetReceiverDetailsQuery,
} from "../../../graphql/generated";
import { v4 as uuid } from "uuid";
import {
  ShippedGoodsPayload,
  NewShipmentInputData,
  ReceivedGoodsPayload,
  ShipmentLocationInputData,
  ShipmentDocumentInputData,
  PredefinedRecurrence,
} from "../Types";
import flatMap from "lodash/flatMap";
import { poundsToKg } from "../../../utils/conversion/weight";
import LocaleProvider from "../../../providers/LocaleProvider";
import { isPickupLocation } from "../../../utils/location/isPickupLocation";

/**
 * Async thunk that fetches the groups flagged with defaultFromReceiverForOrders
 * that the given receiver belongs to, then merges their IDs into shipment.groupIds.
 */
export const fetchAndApplyReceiverDefaultGroups = createAsyncThunk(
  "shipment/fetchAndApplyReceiverDefaultGroups",
  async (receiverId: string) => {
    if (!receiverId) {
      return [];
    }

    const [receiverResult, groupsResult] = await Promise.all([
      useGetReceiverDetailsQuery.fetcher({ id: receiverId })(),
      useGetGroupsQuery.fetcher({
        filter: { defaultFromReceiverForOrders: true },
      })(),
    ]);

    const receiver = receiverResult.receiverById;
    const defaultGroups = groupsResult.groups.data;

    if (!receiver?.groupIds?.length || !defaultGroups.length) {
      return [];
    }

    const receiverGroupIdSet = new Set(receiver.groupIds.map(String));

    return defaultGroups
      .filter((group) => receiverGroupIdSet.has(String(group._id)))
      .map((group) => group._id);
  }
);

export const defaultReceiver: ShipmentLocationInputData = {
  _id: uuid(),
  location: {
    longitude: 0,
    latitude: 0,
  },
  shippedGoods: [],
  locationType: ShipmentLocationType.DropOff,
  receiver: "",
  receivedGoods: [],
  timeWindows: [],
};

export const defaultGood: GoodInput = {
  _id: uuid(),
  label: "",
  quantity: 0,
  weight: LocaleProvider.getWeightUnit() === "lb" ? poundsToKg(1) : 1,
};

export const defaultShipper: ShipmentLocationInputData = {
  _id: uuid(),
  location: {
    longitude: 0,
    latitude: 0,
  },
  shippedGoods: [],
  locationType: ShipmentLocationType.Pickup,
  receiver: "",
  shipper: "",
  receivedGoods: [],
  timeWindows: [],
};

interface ShipmentState {
  shipment: NewShipmentInputData;
  isCommodityDriven: boolean;
  goodProfiles: GoodProfile[];
  storageFacilitiesByReceiverId: Record<
    string,
    GetBusinessEntityDetailsQuery["businessEntityById"]["storageFacilities"]
  >;
  /**
   * Group IDs derived from receiver membership (defaultFromReceiverForOrders).
   * Stored separately so editShipment can always re-merge them, preventing
   * race conditions where a subsequent dispatch overwrites groupIds before
   * the thunk resolves.
   */
  receiverDerivedGroupIds: string[];
}

export const defaultShipmentNote: ShipmentNoteInput = {
  content: "",
  receiver: null,
  shipper: null,
  accessLevel: Object.values(NoteAccessLevel),
};
export const defaultShipmentDocument: ShipmentDocumentInputData = {
  _id: uuid(),
  associatedCharge: "",
  isBillable: false,
  isForRatecons: false,
  isForReceivers: false,
  name: "",
  type: DocumentType.Other,
  url: "",
  file: new File([], ""),
  receiver: null,
  shipper: null,
  accessLevel: Object.values(NoteAccessLevel),
};

export const initialShipment: NewShipmentInputData = {
  id: "",
  assignedCarriers: [],
  constraints: [],
  createdBy: "",
  customer: "",
  documents: [],
  notes: [],
  shipmentLocations: [defaultShipper, defaultReceiver],
  charges: [],
  billOfLadingNumber: null,
  postOfficeNumber: null,
  trailerType: TrailerType.Conestoga,
  loadType: LoadType.FullTruckLoad,
  date: null,
  predefinedRecurrence: PredefinedRecurrence.ONE_TIME,
  commodityType: ShipmentCommodityType.Dry,
};

const initialState: ShipmentState = {
  shipment: initialShipment,
  isCommodityDriven: false,
  goodProfiles: [],
  storageFacilitiesByReceiverId: {},
  receiverDerivedGroupIds: [],
};

const shipmentSlice = createSlice({
  name: "shipper",
  initialState,
  reducers: {
    setCustomer: (state, action: PayloadAction<string>) => {
      state.shipment.customer = action.payload;
    },
    addShipper: (state) => {
      state.shipment.shipmentLocations.push({
        ...defaultShipper,
        _id: uuid(),
        shippedGoods: [{ ...defaultGood, _id: uuid() }],
      });
    },
    addReceiver: (state) => {
      state.shipment.shipmentLocations.push({
        ...defaultReceiver,
        _id: uuid(),
      });
    },
    removeShipperOrReceiver: (state, action: PayloadAction<string>) => {
      const removedShipmentLocation = state.shipment.shipmentLocations.find(
        (shipmentLocation) => shipmentLocation._id === action.payload
      );

      state.shipment.shipmentLocations =
        state.shipment.shipmentLocations.filter(
          (shipmentLocation) => shipmentLocation._id !== action.payload
        );

      const goodIdsToClean = removedShipmentLocation?.shippedGoods.map(
        (shippedGood) => shippedGood._id
      );

      state.shipment.shipmentLocations.forEach((shipmentLocation) => {
        shipmentLocation.receivedGoods = shipmentLocation.receivedGoods.filter(
          (receivedGood) => !goodIdsToClean?.includes(receivedGood.goodId)
        );
      });
    },
    editShipmentLocationInput: (
      state,
      action: PayloadAction<{
        location: ShipmentLocationInputData;
      }>
    ) => {
      const index: number = state.shipment.shipmentLocations.findIndex(
        (shipmentLocation) =>
          shipmentLocation._id === action.payload.location._id
      );
      state.shipment.shipmentLocations[index] = action.payload.location;
    },
    setShipperGoods: (state, action: PayloadAction<ShippedGoodsPayload>) => {
      const index: number = state.shipment.shipmentLocations.findIndex(
        (shipmentLocation) => shipmentLocation._id === action.payload._id
      );
      state.shipment.shipmentLocations[index].shippedGoods =
        action.payload.goods;
      if (state.isCommodityDriven) {
        return;
      }
      const removedGoodIds = state.shipment.shipmentLocations[
        index
      ].shippedGoods
        .filter(
          (shippedGood) =>
            !action.payload.goods.find((good) => good._id === shippedGood._id)
        )
        .map((removedGood) => removedGood._id);
      // update receiver goods when some goods are removed from the form
      state.shipment.shipmentLocations.forEach((shipmentLocation) => {
        shipmentLocation.receivedGoods = shipmentLocation.receivedGoods.filter(
          (receivedGood) => !removedGoodIds.includes(receivedGood.goodId)
        );
      });

      // autofill received goods if there is only 1 receiver
      const dropOffShipmentLocations = state.shipment.shipmentLocations.filter(
        (sl) => sl.locationType === ShipmentLocationType.DropOff
      );
      const correspondingReceiverLocation =
        dropOffShipmentLocations.length === 1
          ? dropOffShipmentLocations[0]
          : null;
      if (correspondingReceiverLocation) {
        const pickupLocations = state.shipment.shipmentLocations.filter(
          (sl) => sl.locationType === ShipmentLocationType.Pickup
        );
        const allShippedGoods = flatMap(
          pickupLocations,
          (sl) => sl.shippedGoods
        );
        correspondingReceiverLocation.receivedGoods = allShippedGoods.map(
          (good) => ({
            goodId: good._id,
            quantity: good.quantity,
          })
        );
      }
    },
    setReceiverGoods: (state, action: PayloadAction<ReceivedGoodsPayload>) => {
      const index = state.shipment.shipmentLocations.findIndex(
        (shipmentLocation) => shipmentLocation._id === action.payload._id
      );
      if (index === -1) {
        return;
      }
      state.shipment.shipmentLocations[index].receivedGoods =
        action.payload.receivedGoods;
      // This is so the whole form gets rerendered
      // eslint-disable-next-line no-self-assign
      state.shipment = state.shipment;
    },
    addCharge: (state, action: PayloadAction<TransactionInput>) => {
      state.shipment.charges.push(action.payload);
    },
    setCharges: (state, action: PayloadAction<TransactionInput[]>) => {
      state.shipment.charges = action.payload;
    },
    editShipment: (state, action: PayloadAction<NewShipmentInputData>) => {
      state.shipment = action.payload;
      // Always re-merge receiver-derived groups so a stale snapshot passed by
      // a concurrent dispatch can never silently drop them.
      if (state.receiverDerivedGroupIds.length) {
        const existing = (state.shipment.groupIds || []).map(String);
        state.shipment.groupIds = Array.from(
          new Set([...existing, ...state.receiverDerivedGroupIds])
        );
      }
      // If you are editing a liquid order, we switch to commodity driven
      if (state.shipment.commodityType === ShipmentCommodityType.Liquid) {
        state.isCommodityDriven = true;
        state.shipment.shipmentLocations =
          state.shipment.shipmentLocations.filter(
            (location) => !isPickupLocation(location)
          );
      }
    },
    resetShipmentForm: (state) => {
      state.shipment = { ...initialShipment };
      state.receiverDerivedGroupIds = [];
    },
    patchShipment: (
      state,
      action: PayloadAction<Partial<NewShipmentInputData>>
    ) => {
      state.shipment = { ...state.shipment, ...action.payload };
    },
    setIsCommodityDriven: (state, action: PayloadAction<boolean>) => {
      state.isCommodityDriven = action.payload;
      state.receiverDerivedGroupIds = [];
      state.shipment = {
        ...initialShipment,
        loadType: state.shipment.loadType,
        commodityType: action.payload
          ? ShipmentCommodityType.Liquid
          : ShipmentCommodityType.Dry,
      };
      // Remove the default shipper location if we are commodity driven
      if (action.payload) {
        state.shipment.shipmentLocations =
          state.shipment.shipmentLocations.filter(
            (location) =>
              !(
                location.locationType === ShipmentLocationType.Pickup &&
                !location.shipper
              )
          );
      } else {
        if (
          !state.shipment.shipmentLocations.find(
            (sl) => sl.locationType === ShipmentLocationType.Pickup
          )
        ) {
          state.shipment.shipmentLocations = [
            defaultShipper,
            ...state.shipment.shipmentLocations,
          ];
        }
      }
    },
    setCommodityType: (
      state,
      action: PayloadAction<{
        commodityType: ShipmentCommodityType;
        trailerType?: TrailerType;
        additionalTrailerTypes?: TrailerType[];
      }>
    ) => {
      const { commodityType, trailerType, additionalTrailerTypes } =
        action.payload;
      state.shipment.commodityType = commodityType;
      if (commodityType === ShipmentCommodityType.Dry) {
        state.shipment.trailerType = trailerType ?? TrailerType.Conestoga;
        state.shipment.additionalTrailerTypes = additionalTrailerTypes ?? [];
      } else {
        state.shipment.trailerType = trailerType ?? TrailerType.Tanker;
        state.shipment.additionalTrailerTypes = additionalTrailerTypes ?? [];
      }
    },
    setGoodProfiles: (state, action: PayloadAction<GoodProfile[]>) => {
      state.goodProfiles = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      fetchAndApplyReceiverDefaultGroups.fulfilled,
      (state, action) => {
        const incomingIds = action.payload.map(String);
        // Accumulate derived IDs (multiple receivers may each contribute groups)
        state.receiverDerivedGroupIds = Array.from(
          new Set([...state.receiverDerivedGroupIds, ...incomingIds])
        );
        // Apply immediately to the current shipment snapshot
        if (incomingIds.length) {
          const existing = (state.shipment.groupIds || []).map(String);
          state.shipment.groupIds = Array.from(
            new Set([...existing, ...state.receiverDerivedGroupIds])
          );
        }
      }
    );
  },
});

export const {
  setCustomer,
  addShipper,
  addReceiver,
  editShipmentLocationInput,
  setShipperGoods,
  editShipment,
  setReceiverGoods,
  resetShipmentForm,
  patchShipment,
  removeShipperOrReceiver,
  setIsCommodityDriven,
  setCommodityType,
  setGoodProfiles,
  addCharge,
  setCharges,
} = shipmentSlice.actions;

export default shipmentSlice.reducer;
